import {
  type HistoryOptions,
  parseAsArrayOf,
  parseAsBook,
  parseAsParty,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from '@vrplatform/query-params';
import { day } from '@vrplatform/utils';
import { useNavigate, useParams } from 'react-router';
import { useCallback, useMemo } from 'react';

export type ProfitAndLossTableFilterType =
  | 'party'
  | 'listing'
  | 'month'
  | 'quarter'
  | 'year';

export type ProfitAndLossTableFilterView = 'non-zero-rows' | 'non-zero-columns';

const VIEWS: ProfitAndLossTableFilterView[] = [
  'non-zero-rows',
  'non-zero-columns',
];

export function useProfitAndLossTableFilter() {
  const goto = useNavigate();

  const { type: t } = useParams<{
    type: ProfitAndLossTableFilterType;
  }>();

  const type = (t || 'party') as ProfitAndLossTableFilterType;

  const defaultDateRange = useMemo(() => {
    const today = day();

    if (type === 'party' || type === 'listing') {
      const lastMonth = today.subtract(1, 'month');
      return `${lastMonth.startOf('month').yyyymmdd()}...${lastMonth.endOf('month').yyyymmdd()}`;
    }

    return `${today.startOf('year').yyyymmdd()}...${today.endOf('year').yyyymmdd()}`;
  }, [type]);

  const [filter, setFilter] = useQueryStates({
    date: parseAsString.withDefault(defaultDateRange),
    listingIds: parseAsArrayOf(parseAsString),
    party: parseAsParty,
    ledger: parseAsBook,
    view: parseAsArrayOf(parseAsStringLiteral(VIEWS)),
  });

  const [s, e] = filter.date?.split('...') || [];
  const [defaultStartDate, defaultEndDate] = defaultDateRange.split('...');

  const setType = useCallback(
    (value: ProfitAndLossTableFilterType) => {
      const search = new URLSearchParams(window.location.search);
      goto(`/reports/profit-and-loss/${value}?${search.toString()}`);
    },
    [goto]
  );

  const startDate =
    s && day(s).isValid() ? day(s).yyyymmdd() : defaultStartDate;
  const endDate = e && day(e).isValid() ? day(e).yyyymmdd() : defaultEndDate;

  const setDate = (
    args: { startDate: string; endDate: string },
    history?: HistoryOptions
  ) => setFilter({ date: `${args.startDate}...${args.endDate}` }, { history });

  return {
    filter: {
      ...filter,
      startDate,
      endDate,
      type,
    },
    setDate,
    setType: (updated: ProfitAndLossTableFilterType) => {
      const current = type;

      const switchedToMonthView =
        current !== 'month' &&
        current !== 'quarter' &&
        current !== 'year' &&
        (updated === 'month' || updated === 'quarter' || updated === 'year');

      const switchedToYearView = current !== 'year' && updated === 'year';

      if (switchedToYearView || switchedToMonthView) {
        const start = day(startDate).startOf('year').yyyymmdd();
        const end = day(startDate).endOf('year').yyyymmdd();
        setDate({ startDate: start, endDate: end }, 'replace').then(() => {
          setType(updated);
        });
      } else {
        setType(updated);
      }
    },
    setFilter: (args: Partial<Omit<typeof filter, 'date'>> | null) => {
      setFilter(args);
    },
  };
}
